import { EChartsOption } from 'echarts';
import { RongoSourceGroup } from './types';

/**
 * Code-based toggle between the two organisation-breakdown data-view layouts:
 *   - 'sections': keep the totals table and append one table per contributing
 *     organisation (the original layout).
 *   - 'flat':     render all rows in a single table with the columns
 *     "Item | Contributing organisation | Count".
 */
export type OrganisationBreakdownLayout = 'sections' | 'flat';

export const ORGANISATION_BREAKDOWN_LAYOUT: OrganisationBreakdownLayout = 'flat';

export interface BreakdownItem {
  name: string;
  value: number;
}

export interface MetricColumn {
  key: string;
  label: string;
}

export interface OrganisationBreakdownOptions {
  /** Build the widget's chart option for a single group's data (used by 'sections'). */
  buildGroupOption: (data: any) => EChartsOption;
  /** Flatten a single group's data into name/value rows (used by the default 'flat' renderer). */
  flatten?: (data: any) => BreakdownItem[];
  /** Optional custom 'flat' renderer (e.g. time-series with multiple metric columns). */
  renderFlat?: (groups: RongoSourceGroup<any>[]) => string;
}

const formatValue = (value: number): string => {
  if (!Number.isFinite(value)) return '-';
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
};

const renderFlatBreakdown = (groups: RongoSourceGroup<any>[], flatten: (data: any) => BreakdownItem[]): string => {
  const rows: { item: string; org: string; value: number }[] = [];
  groups.forEach((group) => {
    flatten(group.data).forEach((item) => {
      rows.push({ item: item.name, org: group.name, value: item.value });
    });
  });

  const body = rows
    .map((row) => `<tr><td>${row.item}</td><td>${row.org}</td><td>${formatValue(row.value)}</td></tr>`)
    .join('');

  return [
    '<h4>By contributing organisation</h4>',
    '<table class="echarts-dataview-table">',
    '<thead><tr><th>Item</th><th>Contributing organisation</th><th>Count</th></tr></thead>',
    `<tbody>${body}</tbody>`,
    '</table>',
  ].join('');
};

/**
 * Render a flat, wide table for time-series groups: one row per (date, group)
 * with a column per metric. Metric cells render "name (value)"; text fields
 * (e.g. dominant water colour) render as-is.
 */
export const renderMetricTable = (groups: RongoSourceGroup<any>[], columns: MetricColumn[]): string => {
  const rows: { date: string; org: string; entry: any }[] = [];
  groups.forEach((group) => {
    Object.entries(group.data).forEach(([date, entry]) => {
      rows.push({ date, org: group.name, entry });
    });
  });

  const header = ['Date', 'Contributing organisation', ...columns.map((c) => c.label)];

  const formatCell = (entry: any, key: string): string => {
    const value = entry[key];
    if (value == null) return '-';
    if (typeof value === 'object' && 'name' in value) {
      const name = String(value.name ?? '');
      if (name === 'N/A' || name === '') return 'N/A';
      return `${name} (${formatValue(value.value)})`;
    }
    return String(value);
  };

  const body = rows
    .map((row) => {
      const cells = columns.map((c) => `<td>${formatCell(row.entry, c.key)}</td>`).join('');
      return `<tr><td>${row.date}</td><td>${row.org}</td>${cells}</tr>`;
    })
    .join('');

  return [
    '<h4>By contributing organisation</h4>',
    '<table class="echarts-dataview-table">',
    `<thead><tr>${header.map((h) => `<th>${h}</th>`).join('')}</tr></thead>`,
    `<tbody>${body}</tbody>`,
    '</table>',
  ].join('');
};

const renderSectionBreakdown = (
  totals: string,
  groups: RongoSourceGroup<any>[],
  buildGroupOption: (data: any) => EChartsOption
): string => {
  const sections = groups
    .map((group) => {
      const groupOption = buildGroupOption(group.data);
      const groupDataView = (groupOption as any)?.toolbox?.feature?.dataView;
      const groupRenderer = groupDataView?.optionToContent || ((option: EChartsOption) => '');
      return `<h4>${group.name}</h4>${groupRenderer(groupOption)}`;
    })
    .join('');

  return `${totals}<hr/><h4>By contributing organisation</h4>${sections}`;
};

/**
 * Wrap an ECharts option so its data view (the toolbox "data view" table) appends
 * a "By contributing organisation" breakdown below the totals table.
 *
 * The breakdown layout is controlled by `ORGANISATION_BREAKDOWN_LAYOUT`:
 * either separate per-organisation tables, or a single flattened table.
 *
 * If there are no groups, or only a single group (i.e. only the reporting
 * organisation itself), the option is returned unchanged.
 */
export const withOrganisationBreakdown = (
  opt: EChartsOption,
  groups: RongoSourceGroup<any>[] | null | undefined,
  options: OrganisationBreakdownOptions
): EChartsOption => {
  if (!groups || groups.length <= 1) return opt;

  const dataView = (opt as any)?.toolbox?.feature?.dataView;
  if (!dataView || typeof dataView.optionToContent !== 'function') return opt;

  const baseRenderer = dataView.optionToContent;

  dataView.optionToContent = (option: EChartsOption): string => {
    if (ORGANISATION_BREAKDOWN_LAYOUT === 'flat') {
      if (options.renderFlat) return options.renderFlat(groups);
      return renderFlatBreakdown(groups, options.flatten || (() => []));
    }
    return renderSectionBreakdown(baseRenderer(option), groups, options.buildGroupOption);
  };

  return opt;
};
